import s from './Offers.module.sass';

import React, { FC, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useRoute, useRouter } from 'react-router5';

import { Banner, Button, Header, Placeholder, ScrollView, Tabs, TabsItem } from '$uikit';
import { offersActions, offersSelector } from '$store/offers';
import { Loader, UnitName, UnitPhoto } from '$shared/components';
import { useCurrentUserInfo, useOffer, useTranslator, useUserInfo } from '$hooks';
import { IcChevron16, IcPlusCircle28 } from '$assets';
import { OFFERS, VIEW_OFFER } from '$shared/constants/pages';
import { CurrencyEnum, OfferStatModel } from '$store/models';
import { formatCurrency } from '$utils';
import { showModal } from '$navigation/helper';
import { batchActions } from '$store';
import { attachActions } from '$store/attach';
import { FeedNotification } from '$core/Feed/FeedNotification/FeedNotification';

const OfferRow: FC<{ offerId: number }> = ({ offerId }) => {
  const offer = useOffer(offerId);
  const user = useUserInfo(offer?.userId ?? 0);
  const t = useTranslator();

  const period = useMemo(() => {
    if (!offer) {
      return 'Unknown';
    }

    if (offer.sharePeriodDays === 7) {
      return t('offer_create_period_7');
    } else if (offer.sharePeriodDays === 30) {
      return t('offer_create_period_30');
    } else if (offer.sharePeriodDays === 90) {
      return t('offer_create_period_90');
    } else if (offer.sharePeriodDays === 180) {
      return t('offer_create_period_180');
    } else if (offer.sharePeriodDays === 365) {
      return t('offer_create_period_365');
    } else {
      return 'Unknown';
    }
  }, [offer?.sharePeriodDays]);

  if (!offer) {
    return null;
  }

  return (
    <Link className={s.offer} routeName={VIEW_OFFER} routeParams={{ id: offerId }}>
      <div className={s.offerCont}>
        <UnitPhoto unit={user} size="small" />
        <div className={s.offerInfoWrap}>
          <div className={s.offerUserName}>
            <UnitName unit={user} />
          </div>
          <div className={s.offerInfo}>
            <span>{t('view_offer_percent')}:</span> {offer.sharePercent}%,{' '}
            <span>{t('view_offer_period')}:</span> {period},{' '}
            <span>{t('view_offer_type')}:</span>{' '}
            {offer.shareType === 1
              ? t('offer_create_type_subscriptions')
              : t('offer_create_type_all')}
          </div>
        </div>
      </div>
      <div className={s.offerChevron}>
        <IcChevron16 />
      </div>
    </Link>
  );
};

const OfferStatRow: FC<{ offerId: number; stat: OfferStatModel }> = ({
  offerId,
  stat,
}) => {
  const { offersInfo } = useSelector(offersSelector);
  const offer = offersInfo[offerId];
  const t = useTranslator();

  return (
    <div className={s.offerStat}>
      <OfferRow offerId={offerId} />
      {offer.isDeleted && <div className={s.offerStatDeleted}>{t('offer_deleted')}</div>}
      <div className={s.offerStatInfo}>
        <div className={s.offerStatInfoRow}>
          <span>{t('offer_views')}:</span> {stat.views}
        </div>
        <div className={s.offerStatInfoRow}>
          <span>{t('offer_registrations')}:</span> {stat.registers}
        </div>
        <div className={s.offerStatInfoRow}>
          <span>{t('offer_earnings')}:</span>{' '}
          {formatCurrency(stat.profit, CurrencyEnum.TON)}
        </div>
      </div>
    </div>
  );
};

export const Offers: FC = () => {
  const dispatch = useDispatch();
  const { loadingStatus, offers, myOffers, stats, offersInfo } =
    useSelector(offersSelector);
  const t = useTranslator();
  const curUser = useCurrentUserInfo();
  const router = useRouter();

  const { route } = useRoute();
  const tab = route.params.tab || 'all';

  const loadData = () => {
    dispatch(offersActions.loadOffers());
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleTabChange = (newTab: string) => {
    const params: any = {};
    if (newTab !== 'all') {
      params.tab = newTab;
    } else {
      delete params.tab;
    }

    router.navigate(OFFERS, params, { replace: true });
  };

  const handleCreate = () => {
    dispatch(batchActions(offersActions.createFormReset(), attachActions.clear('offer')));
    showModal('new_offer');
  };

  const statsPrepared = useMemo(() => {
    const result: { offerId: number; stat: OfferStatModel }[] = [];
    for (let id in stats) {
      result.push({
        offerId: +id,
        stat: stats[id],
      });
    }

    result.sort((a, b) => b.stat.profit - a.stat.profit);

    return result;
  }, [stats]);

  const items = useMemo(() => {
    let items: number[] = [];
    if (tab === 'all') {
      items = [...offers];
    } else if (tab === 'my') {
      items = [...myOffers];
    }

    items.sort((a, b) => {
      const offerA = offersInfo[a];
      const offerB = offersInfo[b];

      if (!offerA || !offerB) {
        return 0;
      }

      return offerB.sharePercent - offerA.sharePercent;
    });

    return items;
  }, [tab, offers, myOffers, offersInfo]);

  function renderTab() {
    if (tab === 'all' || tab === 'my') {
      return (
        <>
          <FeedNotification
            skipClose
            notification={{
              id: 'offers_info',
              title: 'Как это работает?',
              description: 'Оферы позволяют заработать больше денег с вашего трафика.',
              iconUrl: '',
              actionText: 'Подробнее',
              actionUrl: 'https://telegra.ph/Offery-na-TonPlace-12-24',
              action: '',
              actionParams: {},
            }}
          />
          {items.map((offerId) => (
            <OfferRow key={offerId} offerId={offerId} />
          ))}
        </>
      );
    } else if (tab === 'stats') {
      if (statsPrepared.length === 0) {
        return <Placeholder>{t('offer_stats_empty')}</Placeholder>;
      }

      return statsPrepared.map(({ offerId, stat }) => (
        <OfferStatRow key={offerId} offerId={offerId} stat={stat} />
      ));
    }
  }

  function renderContent() {
    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={loadData}>
              {t('failed_button')}
            </Button>
          }
          centered
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (loadingStatus === 'loading' && offers.length === 0) {
      return <Loader isWrapped />;
    }

    return (
      <>
        <Tabs selected={tab} onChange={handleTabChange}>
          <TabsItem value="all">{t('offer_tab_all')}</TabsItem>
          {curUser?.verified === 2 && <TabsItem value="my">{t('offer_tab_my')}</TabsItem>}
          <TabsItem value="stats">{t('offer_tab_stats')}</TabsItem>
        </Tabs>
        {renderTab()}
      </>
    );
  }

  return (
    <>
      <Header
        showBackButton
        rightIcons={[
          curUser?.verified === 2
            ? {
                icon: <IcPlusCircle28 />,
                onClick: handleCreate,
              }
            : null,
        ]}
      >
        {t('offer_title')}
      </Header>
      <ScrollView>{renderContent()}</ScrollView>
    </>
  );
};
